import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import MuiTooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MergeIcon from '@mui/icons-material/Merge';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { getMigrationCodeChanges } from '../../services/apiService';

const REPO = 'tr/highq_migration';
const REPO_URL = 'https://github.com/tr/highq_migration';
const DEFAULT_DAYS = 90;

const BRANCH_PALETTE = [
  '#1D4434', '#1565C0', '#E65100', '#6A1B9A',
  '#00695C', '#C62828', '#0277BD', '#4E342E',
  '#558B2F', '#AD1457', '#00838F', '#F57F17'
];

function getBranchColor(branch, allBranches) {
  const idx = allBranches.indexOf(branch);
  return BRANCH_PALETTE[idx % BRANCH_PALETTE.length];
}

function weekLabel(weekStr) {
  const d = new Date(weekStr + 'T00:00:00Z');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

// Map any date string to the ISO week string (YYYY-MM-DD Monday) it belongs to
function weekOfDate(dateStr) {
  const d = new Date(dateStr);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + diff);
  monday.setUTCHours(0, 0, 0, 0);
  return monday.toISOString().slice(0, 10);
}

function fmtNum(n) {
  if (n == null) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

function buildChartData(weeks, weeklyStats, activeBranches, prMode) {
  return weeks.map((week) => {
    const row = { week, weekLabel: weekLabel(week) };
    let stackTotal = 0;
    for (const branch of activeBranches) {
      const s = weeklyStats[week]?.[branch] || {};
      const prs = prMode === 'merged' ? (s.mergedCount || 0) : (s.prCount || 0);
      row[`${branch}__prs`] = prs;
      row[`${branch}__add`] = s.additions || 0;
      row[`${branch}__del`] = s.deletions || 0;
      row[`${branch}__intensity`] = (s.additions || 0) + (s.deletions || 0);
      stackTotal += prs;
    }
    row['__stackTotal__'] = stackTotal;
    return row;
  });
}

const SummaryCard = ({ label, value, sub, color }) => (
  <Box sx={{ textAlign: 'center', px: 2, py: 1.5, minWidth: 100 }}>
    <Typography variant="h5" fontWeight={700} color={color || 'text.primary'}>{value}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
    {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
  </Box>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Paper sx={{ p: 1.5, fontSize: 12, maxWidth: 240 }}>
      <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>Week of {label}</Typography>
      {payload.map((entry) => (
        <Box key={entry.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color }} />
            <Typography variant="caption">{entry.name}</Typography>
          </Box>
          <Typography variant="caption" fontWeight={600}>
            {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

// ── PR detail modal ────────────────────────────────────────────────────────────
const PRModal = ({ open, title, prs, allBranches, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{prs.length} pull request{prs.length !== 1 ? 's' : ''}</Typography>
      </Box>
      <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ p: 0 }}>
      {prs.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No PRs found for this selection.</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Branch</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Author</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Merged</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">+Added</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">−Removed</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Files</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prs.map((pr) => (
                <TableRow key={pr.number} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <a
                      href={pr.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1565C0', textDecoration: 'none', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 2 }}
                    >
                      #{pr.number} <OpenInNewIcon sx={{ fontSize: 10 }} />
                    </a>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <MuiTooltip title={pr.title} placement="top-start">
                      <Typography variant="body2" noWrap sx={{ fontSize: 12 }}>{pr.title}</Typography>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pr.baseRef}
                      size="small"
                      sx={{ fontSize: 10, height: 18, bgcolor: getBranchColor(pr.baseRef, allBranches), color: '#fff', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{pr.author}</TableCell>
                  <TableCell>
                    <Chip
                      label={pr.merged ? 'merged' : pr.state}
                      size="small"
                      color={pr.merged ? 'success' : pr.state === 'open' ? 'primary' : 'default'}
                      sx={{ fontSize: 10, height: 18 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(pr.createdAt)}</TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{pr.mergedAt ? fmtDate(pr.mergedAt) : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: '#2E7D32', fontWeight: 600 }}>
                    +{pr.additions.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: '#C62828', fontWeight: 600 }}>
                    −{pr.deletions.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{pr.changedFiles ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} size="small">Close</Button>
    </DialogActions>
  </Dialog>
);

export default function MigrationCodeChangesTab() {
  const defaultEnd = dayjs();
  const defaultStart = dayjs().subtract(DEFAULT_DAYS, 'day');

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [prMode, setPrMode] = useState('all');
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage] = useState(20);
  const [tableSort, setTableSort] = useState({ col: 'createdAt', dir: 'desc' });
  const [modal, setModal] = useState({ open: false, title: '', prs: [] });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMigrationCodeChanges(startDate.toDate(), endDate.toDate());
      if (!result.success) throw new Error(result.message || 'Failed to load');
      setData(result.data);
      setSelectedBranches([]);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, []);

  const allBranches = data?.branches || [];
  const activeBranches = selectedBranches.length > 0 ? selectedBranches : allBranches;

  const chartData = useMemo(
    () => buildChartData(data?.weeks || [], data?.weeklyStats || {}, activeBranches, prMode),
    [data, activeBranches, prMode]
  );

  const totals = useMemo(() => {
    if (!data) return {};
    const prs = data.pullRequests || [];
    const merged = prs.filter((p) => p.merged);
    const totalAdd = prs.reduce((s, p) => s + (p.additions || 0), 0);
    const totalDel = prs.reduce((s, p) => s + (p.deletions || 0), 0);
    return {
      totalPRs: prs.length,
      merged: merged.length,
      open: prs.filter((p) => p.state === 'open').length,
      additions: totalAdd,
      deletions: totalDel,
      intensity: totalAdd + totalDel,
      branches: new Set(prs.map((p) => p.baseRef)).size
    };
  }, [data]);

  const sortedPRs = useMemo(() => {
    const prs = (data?.pullRequests || []).filter(
      (p) => !selectedBranches.length || selectedBranches.includes(p.baseRef)
    );
    return [...prs].sort((a, b) => {
      const { col, dir } = tableSort;
      const mul = dir === 'asc' ? 1 : -1;
      if (col === 'number')    return mul * (a.number - b.number);
      if (col === 'createdAt') return mul * (new Date(a.createdAt) - new Date(b.createdAt));
      if (col === 'additions') return mul * ((a.additions || 0) - (b.additions || 0));
      if (col === 'deletions') return mul * ((a.deletions || 0) - (b.deletions || 0));
      if (col === 'intensity') return mul * ((a.additions + a.deletions) - (b.additions + b.deletions));
      return 0;
    });
  }, [data, selectedBranches, tableSort]);

  const handleSort = (col) => {
    setTableSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' });
    setTablePage(0);
  };

  // Collect PRs that belong to a given week bucket, optionally filtered to a branch
  const openModal = useCallback((week, weekLbl, branch = null) => {
    if (!data?.pullRequests) return;
    const prs = data.pullRequests.filter((pr) => {
      const prWeek = weekOfDate(pr.createdAt);
      const weekMatch = prWeek === week;
      const branchMatch = !branch || pr.baseRef === branch;
      const activeMatch = !selectedBranches.length || selectedBranches.includes(pr.baseRef);
      return weekMatch && branchMatch && activeMatch;
    });
    const title = branch
      ? `Week of ${weekLbl} — ${branch}`
      : `Week of ${weekLbl} — all branches`;
    setModal({ open: true, title, prs });
  }, [data, selectedBranches]);

  const closeModal = () => setModal({ open: false, title: '', prs: [] });

  // Show bar/line labels only when there are few data points (prevents crowding)
  const showLabels = chartData.length <= 16;

  const barLabelStyle = { fontSize: 10, fontWeight: 700, fill: '#444' };
  const lineLabelStyle = { fontSize: 9, fontWeight: 600 };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              Code Changes —{' '}
              <MuiTooltip title="Open repository on GitHub">
                <a
                  href={REPO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1D4434', textDecoration: 'none', fontFamily: 'monospace', fontSize: '0.85em' }}
                >
                  {REPO} <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                </a>
              </MuiTooltip>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PR activity, code volume, and change intensity across branches · click any bar or point to see PRs
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <DatePicker
              label="From"
              value={startDate}
              onChange={(v) => v && setStartDate(v)}
              slotProps={{ textField: { size: 'small', sx: { width: 145 } } }}
            />
            <DatePicker
              label="To"
              value={endDate}
              onChange={(v) => v && setEndDate(v)}
              slotProps={{ textField: { size: 'small', sx: { width: 145 } } }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              onClick={load}
              disabled={loading}
              sx={{ bgcolor: '#1D4434', '&:hover': { bgcolor: '#155232' } }}
            >
              {loading ? 'Loading…' : 'Load'}
            </Button>
          </Box>
        </Box>

        {/* Filters row */}
        {data && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220, maxWidth: 340 }}>
              <InputLabel>Branches</InputLabel>
              <Select
                multiple
                value={selectedBranches}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedBranches(typeof v === 'string' ? v.split(',') : v);
                  setTablePage(0);
                }}
                input={<OutlinedInput label="Branches" />}
                renderValue={(sel) =>
                  sel.length === 0 ? 'All branches' : sel.length === 1 ? sel[0] : `${sel.length} branches`
                }
              >
                {allBranches.map((b) => (
                  <MenuItem key={b} value={b} dense>
                    <Checkbox checked={selectedBranches.includes(b)} size="small" />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getBranchColor(b, allBranches), mr: 1, flexShrink: 0 }} />
                    <ListItemText primary={b} primaryTypographyProps={{ fontSize: 13 }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup size="small" exclusive value={prMode} onChange={(_, v) => v && setPrMode(v)}>
              <ToggleButton value="all" sx={{ fontSize: 11, px: 1.5 }}>All PRs</ToggleButton>
              <ToggleButton value="merged" sx={{ fontSize: 11, px: 1.5 }}>
                <MergeIcon sx={{ fontSize: 14, mr: 0.5 }} />Merged only
              </ToggleButton>
            </ToggleButtonGroup>

            {data.truncated && (
              <Chip label="Capped at 250 PRs — narrow the date range for full data" size="small" color="warning" variant="outlined" />
            )}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && !data && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} sx={{ color: '#1D4434' }} />
          </Box>
        )}

        {data && (
          <>
            {/* Summary strip */}
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', divideX: 1 }}>
                <SummaryCard label="Total PRs" value={totals.totalPRs} />
                <Divider orientation="vertical" flexItem />
                <SummaryCard label="Merged" value={totals.merged} color="#1D4434" />
                <Divider orientation="vertical" flexItem />
                <SummaryCard label="Open" value={totals.open} color="#1565C0" />
                <Divider orientation="vertical" flexItem />
                <SummaryCard label="Lines Added" value={fmtNum(totals.additions)} color="#2E7D32" />
                <Divider orientation="vertical" flexItem />
                <SummaryCard label="Lines Removed" value={fmtNum(totals.deletions)} color="#C62828" />
                <Divider orientation="vertical" flexItem />
                <SummaryCard label="Total Volume" value={fmtNum(totals.intensity)} sub="adds + dels" />
                <Divider orientation="vertical" flexItem />
                <SummaryCard label="Active Branches" value={totals.branches} />
              </Box>
            </Paper>

            {chartData.length === 0 ? (
              <Alert severity="info">No PRs found for the selected period and branch filter.</Alert>
            ) : (
              <>
                {/* Chart 1: PR Activity by Branch (Stacked Bar) */}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  PR Activity by Branch — weekly{prMode === 'merged' ? ' (merged only)' : ''}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 24, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {activeBranches.map((branch) => (
                        <Bar
                          key={branch}
                          dataKey={`${branch}__prs`}
                          name={branch}
                          stackId="prs"
                          fill={getBranchColor(branch, allBranches)}
                          cursor="pointer"
                          onClick={(d) => openModal(d.week, d.weekLabel, branch)}
                        />
                      ))}
                      {/* Transparent bar on top of stack to carry total labels */}
                      <Bar
                        dataKey="__stackTotal__"
                        stackId="prs"
                        fill="transparent"
                        legendType="none"
                        isAnimationActive={false}
                        cursor="pointer"
                        onClick={(d) => openModal(d.week, d.weekLabel, null)}
                      >
                        <LabelList
                          dataKey="__stackTotal__"
                          position="top"
                          style={barLabelStyle}
                          formatter={(v) => (v > 0 ? v : '')}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>

                {/* Chart 2: Code Change Frequency (Line chart) */}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Code Change Frequency — PRs per week by branch
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={chartData} margin={{ top: 20, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {activeBranches.map((branch) => (
                        <Line
                          key={branch}
                          type="monotone"
                          dataKey={`${branch}__prs`}
                          name={branch}
                          stroke={getBranchColor(branch, allBranches)}
                          strokeWidth={2}
                          dot={chartData.length <= 20}
                          activeDot={{
                            r: 5,
                            cursor: 'pointer',
                            onClick: (_, payload) => openModal(payload.payload.week, payload.payload.weekLabel, branch)
                          }}
                        >
                          {showLabels && (
                            <LabelList
                              dataKey={`${branch}__prs`}
                              position="top"
                              style={{ ...lineLabelStyle, fill: getBranchColor(branch, allBranches) }}
                              formatter={(v) => (v > 0 ? v : '')}
                            />
                          )}
                        </Line>
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>

                {/* Chart 3: Code Change Intensity (Area chart) */}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Code Change Intensity — lines added + deleted per week
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData} margin={{ top: 20, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {activeBranches.map((branch) => (
                        <Area
                          key={branch}
                          type="monotone"
                          dataKey={`${branch}__intensity`}
                          name={branch}
                          stroke={getBranchColor(branch, allBranches)}
                          fill={getBranchColor(branch, allBranches)}
                          fillOpacity={0.15}
                          strokeWidth={2}
                          stackId={activeBranches.length > 3 ? 'intensity' : undefined}
                          activeDot={{
                            r: 5,
                            cursor: 'pointer',
                            onClick: (_, payload) => openModal(payload.payload.week, payload.payload.weekLabel, branch)
                          }}
                        >
                          {showLabels && (
                            <LabelList
                              dataKey={`${branch}__intensity`}
                              position="top"
                              style={{ ...lineLabelStyle, fill: getBranchColor(branch, allBranches) }}
                              formatter={(v) => (v > 0 ? fmtNum(v) : '')}
                            />
                          )}
                        </Area>
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>

                {/* Chart 4: Additions vs Deletions trend */}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Additions vs Deletions trend — across selected branches
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={chartData.map((row) => {
                        const totalAdd = activeBranches.reduce((s, b) => s + (row[`${b}__add`] || 0), 0);
                        const totalDel = activeBranches.reduce((s, b) => s + (row[`${b}__del`] || 0), 0);
                        return { ...row, totalAdditions: totalAdd, totalDeletions: -totalDel };
                      })}
                      margin={{ top: 24, right: 16, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNum(Math.abs(v))} />
                      <Tooltip
                        formatter={(v, name) => [Math.abs(v).toLocaleString(), name === 'totalDeletions' ? 'Deletions' : 'Additions']}
                        labelFormatter={(l) => `Week of ${l}`}
                      />
                      <Legend formatter={(v) => (v === 'totalDeletions' ? 'Deletions' : 'Additions')} wrapperStyle={{ fontSize: 12 }} />
                      <Bar
                        dataKey="totalAdditions"
                        name="totalAdditions"
                        fill="#2E7D32"
                        cursor="pointer"
                        onClick={(d) => openModal(d.week, d.weekLabel, null)}
                      >
                        <LabelList
                          dataKey="totalAdditions"
                          position="top"
                          style={{ ...barLabelStyle, fill: '#2E7D32' }}
                          formatter={(v) => (v > 0 ? fmtNum(v) : '')}
                        />
                      </Bar>
                      <Bar
                        dataKey="totalDeletions"
                        name="totalDeletions"
                        fill="#C62828"
                        cursor="pointer"
                        onClick={(d) => openModal(d.week, d.weekLabel, null)}
                      >
                        <LabelList
                          dataKey="totalDeletions"
                          position="bottom"
                          style={{ ...barLabelStyle, fill: '#C62828' }}
                          formatter={(v) => (v < 0 ? fmtNum(Math.abs(v)) : '')}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>

                {/* PR Table */}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Pull Requests ({sortedPRs.length})
                </Typography>
                <Paper variant="outlined" sx={{ mb: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            <TableSortLabel
                              active={tableSort.col === 'number'}
                              direction={tableSort.col === 'number' ? tableSort.dir : 'desc'}
                              onClick={() => handleSort('number')}
                            >#</TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            <TableSortLabel
                              active={tableSort.col === 'createdAt'}
                              direction={tableSort.col === 'createdAt' ? tableSort.dir : 'desc'}
                              onClick={() => handleSort('createdAt')}
                            >Created</TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                            <TableSortLabel
                              active={tableSort.col === 'additions'}
                              direction={tableSort.col === 'additions' ? tableSort.dir : 'desc'}
                              onClick={() => handleSort('additions')}
                            >
                              <AddCircleOutlineIcon sx={{ fontSize: 13, mr: 0.3, color: '#2E7D32' }} />Added
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                            <TableSortLabel
                              active={tableSort.col === 'deletions'}
                              direction={tableSort.col === 'deletions' ? tableSort.dir : 'desc'}
                              onClick={() => handleSort('deletions')}
                            >
                              <RemoveCircleOutlineIcon sx={{ fontSize: 13, mr: 0.3, color: '#C62828' }} />Removed
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                            <TableSortLabel
                              active={tableSort.col === 'intensity'}
                              direction={tableSort.col === 'intensity' ? tableSort.dir : 'desc'}
                              onClick={() => handleSort('intensity')}
                            >Volume</TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedPRs
                          .slice(tablePage * tableRowsPerPage, tablePage * tableRowsPerPage + tableRowsPerPage)
                          .map((pr) => (
                            <TableRow key={pr.number} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <a
                                  href={pr.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#1565C0', textDecoration: 'none', fontSize: 12 }}
                                >
                                  #{pr.number}
                                </a>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 280 }}>
                                <MuiTooltip title={pr.title} placement="top">
                                  <Typography variant="body2" noWrap sx={{ fontSize: 12 }}>{pr.title}</Typography>
                                </MuiTooltip>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={pr.baseRef}
                                  size="small"
                                  sx={{ fontSize: 10, height: 18, bgcolor: getBranchColor(pr.baseRef, allBranches), color: '#fff', fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{pr.author}</TableCell>
                              <TableCell>
                                <Chip
                                  label={pr.merged ? 'merged' : pr.state}
                                  size="small"
                                  color={pr.merged ? 'success' : pr.state === 'open' ? 'primary' : 'default'}
                                  sx={{ fontSize: 10, height: 18 }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                {new Date(pr.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, color: '#2E7D32' }}>
                                +{pr.additions.toLocaleString()}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, color: '#C62828' }}>
                                -{pr.deletions.toLocaleString()}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 12 }}>
                                {(pr.additions + pr.deletions).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={sortedPRs.length}
                    page={tablePage}
                    onPageChange={(_, p) => setTablePage(p)}
                    rowsPerPage={tableRowsPerPage}
                    rowsPerPageOptions={[20]}
                  />
                </Paper>
              </>
            )}
          </>
        )}
      </Box>

      <PRModal
        open={modal.open}
        title={modal.title}
        prs={modal.prs}
        allBranches={allBranches}
        onClose={closeModal}
      />
    </LocalizationProvider>
  );
}
